import { BehaviorSubject, defer, filter, firstValueFrom, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, shareReplay, tap } from 'rxjs/operators';

/**
 * @class TtlCacheStore<T>
 * @description A cached storage for data of type T, that can be accessed via state$ observable.
 * The data is automatically loaded and refreshed according to the TTL (time to live) specified in the constructor.
 * In case of initialization errors, an initial / default value is returned.
 *
 * Data flow documentation:
 * When you call load() (or load(true)), which results in an API request and call set() before the API has responded,
 * the set() call will be overwritten once the APIs response arrives.
 */
export class TtlCacheStore<T> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static caches: TtlCacheStore<any>[] = [];
    // members:
    private readonly subject: BehaviorSubject<T>;
    private cache$?: Observable<T>;
    private cacheValidUntil = 0;
    private inFlight = false;
    private isInitialized = false;
    private wasReset = false; // object was reset

    /**
     * Creates a new TtlCacheStore instance.
     * It stores a reference to itself internally in a registry that can be used to reset all caches.
     * Attention: This is safe to be used as is in singleton services (e.g., UserService), but when you intend
     * to use in any component or other module that does not exist for the whole application lifecycle,
     * you should set noRegistry to true when constructing the instance. Otherwise, this can cause memory leaks.
     *
     * @param name The name of the cache. This is used for debugging purposes.
     * @param initialValue The initial value to be stored in the cache. This will be returned by get() before the first load.
     * @param ttlMs The time to live (TTL) in milliseconds. After this time, the cache will be refreshed.
     * @param loader A function that returns an Observable that emits the data to be cached.
     * @param noRegistry If true, the cache will not be registered internally to ease cache resets by calling resetAll().
     */
    constructor(
        private readonly name: string,
        private readonly initialValue: T,
        private readonly ttlMs: number,
        private readonly loader: () => Observable<T>,
        noRegistry: boolean = false,
    ) {
        this.subject = new BehaviorSubject<T>(initialValue);
        if (!noRegistry) {
            TtlCacheStore.caches.push(this);
        }
    }

    /**
     * state$ is the observable that emits the current value of the cache.
     * It is automatically initialized with the value returned from the loader function on the first subscription.
     */
    readonly state$ = defer(() => {
        if (!this.isInitialized || this.needsRefresh()) {
            this.load().subscribe({ error: () => {} });
        }
        return this.subject.asObservable().pipe(
            // only emit when the object is initialized, do not return the empty / default value
            // TODO skipWhile() vs filter() - what is the desired behavior for reset?
            filter(() => this.isInitialized),
        );
    });

    /**
     * Clears and invalidates the cache.
     * On the next subscription to the subject or load method, the data will be freshly loaded and cached.
     * This method does not notify any subscribers.
     * Intended usage: Invalidation after modification (see tiles).
     */
    public invalidate(): void {
        this.cache$ = undefined;
        this.cacheValidUntil = 0;
    }

    /**
     * Resets / clears the cache.
     * On the next subscription to the subject or load method, the cache will be freshly loaded.
     * This method updates all current subscribers with the default / empty value.
     * Intended usage: Logout.
     */
    public reset(): void {
        this.invalidate();
        this.subject.next(this.initialValue); // notify all subscribers and reset their data, then reset initialized to avoid further emits with empty data
        this.isInitialized = false;
        this.wasReset = true;
    }

    /**
     * Refreshs the cache.
     * Asynchronous method emits load(true) and returns a promise.
     * @returns A promise of the refreshed and now cached object
     */
    public async refresh(): Promise<T> {
        return firstValueFrom(this.load(true));
    }

    /** Force reload (and updates state) */
    public load(force = false): Observable<T> {
        this.wasReset = false;
        const expired = Date.now() > this.cacheValidUntil;

        if (!force && this.isInitialized && !expired) {
            return of(this.subject.getValue());
        }

        // if a request is currently performed, return that requests observable, this will avoid multiple parallel requests.
        if (this.inFlight && this.cache$) {
            return this.cache$;
        }

        this.cache$ = this.loader().pipe(
            tap((data) => {
                if (this.wasReset) {
                    return;
                }
                // on success: update state
                this.isInitialized = true;
                this.subject.next(data);
                // refresh TTL from *actual* response time
                this.cacheValidUntil = Date.now() + this.ttlMs;
            }),
            shareReplay({ bufferSize: 1, refCount: false }),
            catchError((err) => {
                // on error: allow retry next time
                this.invalidate();
                return throwError(() => err);
            }),
            finalize(() => {
                this.inFlight = false;
            }),
        );

        // prevent race: mark "valid until" early so concurrent calls won't start a second request
        this.cacheValidUntil = Date.now() + this.ttlMs;
        this.inFlight = true;

        return this.cache$;
    }

    private needsRefresh(): boolean {
        return Date.now() > this.cacheValidUntil;
    }

    /**
     * Sets the current value directly (optimistic/local update).
     * - marks the store initialized
     * - refreshes TTL starting now
     * - does NOT trigger any HTTP load
     */
    public set(value: T, refreshTtl: boolean = true): void {
        this.isInitialized = true;
        this.subject.next(value);

        if (refreshTtl) {
            this.cacheValidUntil = Date.now() + this.ttlMs;
        }

        // Optional policy: a manual set should invalidate the in-flight/request cache
        // so later load() calls don't hand out an old request.
        this.cache$ = undefined;
        this.inFlight = false;
    }

    /**
     * Returns the currently cached value.
     */
    public get(): T {
        return this.subject.getValue();
    }

    public getCached(): Observable<T> {
        return this.subject.asObservable();
    }

    public async getAsync(): Promise<T> {
        return firstValueFrom(this.load(false));
    }

    /**
     * @returns true if the store is initialized and ready to be used, otherwise false
     */
    public ready(): boolean {
        return this.isInitialized;
    }

    /**
     * This method resets all caches.
     */
    public static resetAll(): void {
        for (const cache of TtlCacheStore.caches) {
            cache.reset();
        }
    }

    /**
     * This method refreshs all caches.
     */
    public static async refreshAll(): Promise<void> {
        const refreshPromises = TtlCacheStore.caches.map((cache) => cache.refresh());
        const refreshResults = await Promise.allSettled(refreshPromises);
        refreshResults.forEach((result, index) => {
            const cache = TtlCacheStore.caches[index];
            if (result.status === 'fulfilled') {
                console.debug(`Cache "${cache.name}" refreshed.`);
            } else {
                // result.reason enthält den Fehler
                console.error(`Cache "${cache.name}" refresh failed:`, result.reason);
            }
        });
    }
}
